import {
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { PageProps, graphql } from "gatsby";
import React from "react";
import { useForm } from "react-hook-form";
import Layout from "../components/Layout";
import Loader from "../components/Loader";
import InputWithLabel from "../components/forms/InputWithLabel";
import TextAreaWithLabel from "../components/forms/TextAreWithLabel";
import { trackCustomEvent } from "../utils/analytics";
import getCountryCodeFromCountry from "../utils/getCountryCodeFromCountry";

const COUNTRY = process.env.GATSBY_COUNTRY;
const DOMAIN = process.env.GATSBY_DOMAIN;
const countryCode = getCountryCodeFromCountry();

type SubmitContactFormInput = {
  email: string;
  name: string;
  message: string;
};

type SubmitContactFormBody = {
  emailAddress: string;
  name: string;
  message: string;
  countryCode: string;
  originDomain: string;
  userAgent: string;
};

async function submitContactForm(data: SubmitContactFormInput) {
  const { email, name, message } = data;
  const body: SubmitContactFormBody = {
    name,
    message,
    countryCode,
    emailAddress: email,
    originDomain: `${COUNTRY}.${DOMAIN}`, // eg us.best-jobs-online.com
    userAgent: navigator.userAgent,
  };

  const response = await fetch(`https://api-v2.${DOMAIN}/contact-us`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    // pipe through original FormData
    return data;
  }

  const json = await response.json();
  throw new Error(JSON.stringify(json.errors));
}

export default function Contact({
  data: { footerTx, cookieBannerTx, site, tx },
  pageContext,
  location,
}: PageProps<Queries.ContactPageQuery>) {
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<SubmitContactFormInput>({ mode: "onBlur" });

  const { mutate, isLoading, isSuccess, isError } = useMutation(
    submitContactForm,
    {
      onSuccess: () => {
        trackCustomEvent({
          category: "Contact",
          action: `Contact submission success`,
          label: location.pathname,
        });
      },
      onError: (error: Error) => {
        trackCustomEvent({
          category: "Contact",
          action: `Contact submission failed`,
          label: error.message,
        });
      },
    }
  );

  return (
    <Layout
      location={location}
      title={tx!.title!}
      description={tx!.subtitle!}
      footerTx={footerTx}
      cookieBannerTx={cookieBannerTx}
      className="relative mx-auto w-full"
      pageContext={pageContext}
      site={site}
    >
      {isError && (
        <div
          className="flex items-start justify-center rounded-md bg-red-50 p-12"
          data-cy="optout-error"
        >
          <XCircleIcon className="h-7 w-7 text-yellow-400" aria-hidden="true" />
          <h3 className="text-md ml-3 font-medium text-yellow-800">
            {tx!.error}
          </h3>
        </div>
      )}
      <div className="my-12 px-12 text-center sm:mx-auto sm:max-w-lg sm:px-0">
        <h1 className="text-center text-4xl font-bold tracking-tight sm:text-6xl">
          {tx!.title}
        </h1>

        {isSuccess ? (
          <p className="mt-8 flex items-center justify-center text-center">
            <CheckCircleIcon className="mr-2 w-6" />
            {tx!.thanks}
          </p>
        ) : (
          <>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {tx!.subtitle}
            </p>
            <form
              method="get"
              className="mt-16 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
              onSubmit={handleSubmit((data) => mutate(data))}
            >
              <InputWithLabel
                name="name"
                type="text"
                touchedFields={touchedFields}
                errors={errors}
                register={register}
                tx={tx}
                placeholder={tx!.nameLabel!}
                Icon={UserIcon}
                required
              />
              <InputWithLabel
                name="email"
                type="email"
                touchedFields={touchedFields}
                errors={errors}
                register={register}
                tx={tx}
                placeholder={tx!.emailLabel!}
                Icon={EnvelopeIcon}
                required
              />
              <TextAreaWithLabel
                Icon={PhoneIcon}
                name={"message"}
                touchedFields={touchedFields}
                errors={errors}
                placeholder={tx!.messageLabel!}
                register={register}
                required
                tx={tx}
                type="text"
              />
              <button
                type="submit"
                className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 disabled:bg-opacity-40 sm:col-span-2"
                disabled={isLoading}
              >
                {isLoading && <Loader className="mr-2" />}
                {tx!.submit!}
              </button>
            </form>
          </>
        )}
      </div>
    </Layout>
  );
}

export const query = graphql`
  query ContactPage {
    tx: translations(id: { eq: "Contact" }) {
      title
      subtitle
      nameLabel
      emailLabel
      messageLabel
      submit
      thanks
      error
    }
    footerTx: translations(id: { eq: "Footer" }) {
      ...Footer
    }
    cookieBannerTx: translations(id: { eq: "CookieBanner" }) {
      ...CookieBanner
    }
    cpaModalTx: translations(id: { eq: "CpaModal" }) {
      ...CpaModal
    }
    hostedApplyTx: translations(id: { eq: "HostedApply" }) {
      ...HostedApply
    }
    site {
      ...SiteMetadata
    }
  }
`;
